<script setup>
import { watch, ref, onMounted } from 'vue';
import each from 'lodash-es/each';
import stringify from 'qs/lib/stringify';

import { useStore } from '@/store/Store.js';

// import Colors from '@/config/Colors.json';

const props = defineProps({
    center: { type: Boolean, default: false },
    data: { type: Object, default: () => {} },
    color: {
        type: String,
        default: '',
    },
});

const store = useStore();
const content = ref(null);
const isVisible = ref(false);
const isValid = ref(true);
const rootEl = ref(null);

const call = async (link, opts = {}) => {
    const res = await fetch(link, opts);

    if (res.ok) {
        const json = await res.json();
        return json;
    }
    throw new Error('Error detected when viewing ad.');
};

const openLink = (id, url) => {
    // Set data
    const data = { id };
    data[window.csrfTokenName] = window.csrfTokenValue; // Append CSRF Token

    // Open link in new window
    window.open(url);

    // Tally click
    call('/actions/ad-wizard/tracking/click', {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    })
        .then(() => {})
        .catch(() => {});
};

const obs = new IntersectionObserver(
    (entries) => {
        each(entries, (entry) => {
            if (entry && entry.isIntersecting) {
                obs.unobserve(entry.target);
                // window.setTimeout(() => {
                isVisible.value = true;
                // }, 7000)
            }
        });
    },
    {
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
    },
);

onMounted(() => {
    obs.observe(rootEl.value);
});

const adWasDisplayed = (id) => {
    const data = {};
    data[window.csrfTokenName] = window.csrfTokenValue;
    // let dataParams = new URLSearchParams(data);
    const dataParams = stringify(data);
    // // call add displayed
    call(`/posters/${id}/displayed?lang=${store.lang.value}&${dataParams}`, {
        method: 'GET',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
        },
    }).catch(() => {});
};

watch(isVisible, (val) => {
    const mainData = {
        ref: window.location.href.split('?')[0],
    };
    if (val === true) {
        if (window.csrfTokenName) {
            mainData[window.csrfTokenName] = window.csrfTokenValue;
        }
        if (props.data && props.data.data && props.data.data.ad && props.data.data.ad !== '') {
            // Call for single selected ad
            // let mainDataParams = new URLSearchParams(mainData);
            const mainDataParams = stringify(mainData);
            call(`/posters/${props.data.data.ad}?${mainDataParams}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
            })
                .then((json) => {
                    if (json && json.image && json.image.url) {
                        content.value = json;
                        adWasDisplayed(content.value.id);
                    } else {
                        throw new Error('The current ad id must be inactive.');
                    }
                })
                .catch(() => {
                    isValid.value = false;
                });
        } else {
            mainData.adFilters = props.data.adFilters;
            const mainDataParams = stringify(mainData);
            // let mainDataParams = new URLSearchParams(mainData);
            // Call for AdSpace
            call(`/posters?lang=${store.lang.value}&${mainDataParams}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
            })
                .then((json) => {
                    if (json.data.length === 0) {
                        throw new Error('No ad with current parameters.');
                    } else {
                        content.value = json.data[0];
                        adWasDisplayed(content.value.id);
                    }
                })
                .catch(() => {
                    isValid.value = false;
                });
        }
    }
});
</script>

<template>
    <div
        ref="rootEl"
        class="Mm print:hidden"
        :class="['is-' + color, { 'is-centered': center }]"
    >
        <div
            v-if="isValid && isVisible && content && content.url && content.image && (content?.image?.srcset || content?.image?.url)"
            class="Mm__pad py-5 md:py-8"
        >
            <div
                class="Mm__wrap relative flex justify-center bg-white/45 px-2 py-9 text-center md:max-w-[1172px] md:rounded-2xl"
                :class="{ 'Page__right is-l': !center, 'md:mx-auto': center }"
            >
                <span class="Mm__title absolute right-3 top-3 text-xs text-dark-blue/45 md:right-5">Publicité</span>
                <div class="Mm__inner flex items-center justify-center overflow-hidden md:rounded-2xl">
                    <img
                        class="shrink-0 grow-0 cursor-pointer"
                        loading="lazy"
                        :width="content.image.width"
                        :height="content.image.height"
                        :src="content.image.url"
                        :srcset="content?.image?.srcset ? content.image.srcset : null"
                        :alt="content.image.title"
                        v-on:click="openLink(content.id, content.url)"
                    />
                    <template v-if="content.imgTrk">
                        <div v-html="content.imgTrk" />
                    </template>
                </div>
            </div>
        </div>
        <div v-else-if="isValid">
            <div
                class="Mm__pad py-5 md:py-8"
                :class="{ 'Page__right is-l md:mx-auto': !center }"
            >
                <div class="Mm__wrap relative flex justify-center bg-white/45 px-2 py-9 text-center md:max-w-[1172px] md:rounded-2xl">
                    <div class="mx-[612px] my-0 w-full">
                        <div class="Mm__mock w-full bg-light-gray pb-[73%]" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
